import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import Reveal from "./Reveal";

const RATES_URL =
  process.env.REACT_APP_RATES_URL || `${process.env.REACT_APP_BACKEND_URL}/api/rates`;

const fmtBRL = (v) =>
  typeof v === "number"
    ? v.toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
        minimumFractionDigits: 4,
        maximumFractionDigits: 4,
      })
    : "—";

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso.replace(" ", "T"));
  return d.toLocaleString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const Rates = () => {
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ["ptax-rates"],
    queryFn: async () => (await axios.get(RATES_URL)).data,
    refetchInterval: 300000,
    retry: 1,
  });

  return (
    <section
      id="cotacoes"
      className="border-t border-ink/15 bg-white px-6 py-24 lg:px-12 lg:py-36"
      data-testid="rates-section"
    >
      <Reveal>
        <div className="mb-16 flex flex-col gap-4 lg:mb-24">
          <span className="font-mono text-xs uppercase tracking-[0.35em] text-ink/50">
            02 / Cotações
          </span>
          <h2 className="max-w-4xl font-oswald text-4xl font-bold uppercase leading-none tracking-tight sm:text-5xl lg:text-7xl">
            PTAX — Banco Central
          </h2>
          <p className="max-w-xl text-base font-light leading-relaxed text-ink/60">
            Cotações oficiais de fechamento do Banco Central do Brasil para Dólar,
            Euro e Libra Esterlina. Atualização automática.
          </p>
        </div>
      </Reveal>

      <Reveal delay={0.1}>
        <div className="border border-ink/15" data-testid="rates-table">
          <div className="grid grid-cols-2 gap-4 border-b border-ink/15 px-6 py-4 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40 md:grid-cols-12">
            <span className="md:col-span-4">Moeda</span>
            <span className="hidden md:col-span-3 md:block">Compra</span>
            <span className="hidden md:col-span-3 md:block">Venda</span>
            <span className="hidden md:col-span-2 md:block">Referência</span>
          </div>

          {isLoading &&
            ["USD", "EUR", "GBP"].map((code) => (
              <div
                key={code}
                className="grid grid-cols-2 gap-4 border-b border-ink/10 px-6 py-8 last:border-b-0 md:grid-cols-12"
                data-testid={`rate-skeleton-${code.toLowerCase()}`}
              >
                <div className="h-6 w-24 animate-pulse bg-ink/10 md:col-span-4" />
                <div className="h-6 w-20 animate-pulse bg-ink/10 md:col-span-3" />
                <div className="h-6 w-20 animate-pulse bg-ink/10 md:col-span-3" />
                <div className="hidden h-6 w-28 animate-pulse bg-ink/10 md:col-span-2 md:block" />
              </div>
            ))}

          {isError && !isLoading && (
            <div className="flex flex-col items-start gap-6 px-6 py-16" data-testid="rates-error">
              <p className="font-mono text-sm uppercase tracking-[0.2em] text-ink/60">
                Não foi possível carregar as cotações agora.
              </p>
              <button
                onClick={() => refetch()}
                data-testid="rates-retry-button"
                className="border border-ink/40 px-6 py-3 font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-navy hover:text-white"
              >
                Tentar novamente
              </button>
            </div>
          )}

          {data?.currencies?.map((c) => (
            <div
              key={c.code}
              className="group grid grid-cols-2 items-baseline gap-4 border-b border-ink/10 px-6 py-8 transition-colors duration-300 last:border-b-0 hover:bg-navy md:grid-cols-12"
              data-testid={`rate-row-${c.code.toLowerCase()}`}
            >
              <div className="flex items-baseline gap-4 md:col-span-4">
                <span className="font-oswald text-3xl font-bold tracking-tight transition-colors duration-300 group-hover:text-white lg:text-4xl">
                  {c.code}
                </span>
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-ink/50 transition-colors duration-300 group-hover:text-ink/60">
                  {c.symbol} — {c.name}
                </span>
              </div>
              <div className="md:col-span-3">
                <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40 transition-colors duration-300 group-hover:text-white/50 md:hidden">
                  Compra
                </span>
                <span
                  className="font-mono text-lg transition-colors duration-300 group-hover:text-white lg:text-xl"
                  data-testid={`rate-compra-${c.code.toLowerCase()}`}
                >
                  {fmtBRL(c.compra)}
                </span>
              </div>
              <div className="md:col-span-3">
                <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-ink/40 transition-colors duration-300 group-hover:text-white/50 md:hidden">
                  Venda
                </span>
                <span
                  className="font-mono text-lg transition-colors duration-300 group-hover:text-white lg:text-xl"
                  data-testid={`rate-venda-${c.code.toLowerCase()}`}
                >
                  {fmtBRL(c.venda)}
                </span>
              </div>
              <span className="col-span-2 font-mono text-xs text-ink/40 transition-colors duration-300 group-hover:text-white/50 md:col-span-2">
                {fmtDate(c.data_hora)}
              </span>
            </div>
          ))}
        </div>
      </Reveal>

      <Reveal delay={0.15}>
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-ink/40" data-testid="rates-updated-at">
            Fonte: {data?.source ?? "Banco Central do Brasil — PTAX"}
            {data?.fetched_at ? ` — atualizado em ${fmtDate(data.fetched_at)}` : ""}
          </p>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            data-testid="rates-refresh-button"
            className="flex items-center gap-3 border border-ink/40 px-5 py-2 font-mono text-xs uppercase tracking-[0.25em] transition-colors duration-300 hover:bg-navy hover:text-white disabled:opacity-50"
          >
            <RefreshCw size={13} className={isFetching ? "animate-spin" : ""} />
            Atualizar
          </button>
        </div>
      </Reveal>
    </section>
  );
};

export default Rates;
